import { useState, useEffect, ReactNode, FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { Spin } from 'antd';

import { Global } from '../constants';
import { getItem, removeItem } from '../utils/localStorageControl';
import { userGetData } from '../redux/users/actionCreator';

interface ProtectedRouteProps {
  children: ReactNode;
  route: string;
  userGetData: Function;
}

const ProtectedRoute: FC<ProtectedRouteProps> = ({ children, route, userGetData }) => {
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<'checking' | 'allowed' | 'denied'>('checking');

  useEffect(() => {
    const init = async () => {
      try {
        const accessToken = getItem('access_token');
        const refreshToken = getItem('refresh_token');
        const localUser = getItem('user');
        const isAuthenticated = !!(accessToken && refreshToken && localUser);

        const unprotectedRoutes = Global.UNPROTECTED_ROUTES;
        const accessRoutes = Global.ACCESS_ROUTES;

        const containsUnprotected = unprotectedRoutes.some((p) => route.startsWith(p));
        const isAccessRoute = route === '/' || accessRoutes.some((p) => route.startsWith(p));
        const isProtected = route !== '/' && !containsUnprotected;

        if (!isAuthenticated && isProtected) {
          navigate('/');
          setAuthState('denied');
          return;
        }

        if (isAuthenticated && isAccessRoute) {
          navigate('/admin');
          setAuthState('denied');
          return;
        }

        if (!isAuthenticated || !isProtected) {
          setAuthState('allowed');
          return;
        }

        try {
          await userGetData();
        } catch {
          removeItem('access_token');
          removeItem('refresh_token');
          removeItem('user');
          navigate('/');
          setAuthState('denied');
          return;
        }

        setAuthState('allowed');
      } catch {
        setAuthState('allowed');
      }
    };

    init();
  }, [route]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (authState === 'checking') {
        setAuthState('allowed');
      }
    }, 4000);
    return () => clearTimeout(timeout);
  }, [authState]);

  if (authState === 'checking') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="Verificando acceso..." />
      </div>
    );
  }

  if (authState === 'denied') {
    return null;
  }

  return <>{children}</>;
};

export default connect(null, { userGetData })(ProtectedRoute);
